import API from "../services/rest/api";
import { DIET_STATUS } from "../constants/diet";
import { generateDietPlanPdfFileFromData } from "../molecules/DietPlanPdf/generateDietPlanPdfFileFromData";
import { bulkUploadPatientRecordsRequest } from "../store/slices/HealthCoachSlice";
import { AppDispatch } from "../store/store";
import {
  buildBulkUploadFormData,
  buildDietPlanRecordPayload,
} from "./bulkUploadPatientRecords";
import { transformFormDietPlanForPdf } from "./transformDietPlanForPdf";

type UploadDietPlanRecordParams = {
  planStatus: string;
  apiResponse: any;
  dietPlanData: any;
  requestPayload?: any;
  fallbackPatientId?: string | null;
  userDetails?: any;
};

const getHealthCoachId = (userDetails?: any) =>
  userDetails?.health_coach_id || localStorage.getItem("health_coach_id");

const getLoggedInCoachDetails = (userDetails?: any) => {
  let coach = userDetails;

  if (!coach?.health_coach_id || (!coach?.first_name && !coach?.last_name)) {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (storedUser) {
        coach = { ...JSON.parse(storedUser), ...coach };
      }
    } catch {
      // ignore invalid localStorage userInfo
    }
  }

  const assignedBy =
    coach?.health_coach_id || localStorage.getItem("health_coach_id") || undefined;
  const assignedByName = [coach?.first_name, coach?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    assignedBy,
    assignedByName: assignedByName || undefined,
  };
};

const hasMealPlanContent = (data: any) => {
  if (!data) {
    return false;
  }

  if (data.diet_plan_meal_rel?.length) {
    return data.diet_plan_meal_rel.some((meal: any) =>
      meal?.meal_options?.some((option: any) => option?.option_data?.length),
    );
  }

  if (data.meals?.length) {
    return data.meals.some((meal: any) =>
      meal?.options?.some((option: any) =>
        option?.food_items?.some(
          (item: any) => item?.food_item_name && item?.food_item_name !== "",
        ),
      ),
    );
  }

  if (data.meals_data?.length) {
    return data.meals_data.some((meal: any) => meal?.options_data?.length);
  }

  return false;
};

const sanitizeListFormatPlan = (dietPlanData: any) => ({
  ...dietPlanData,
  diet_plan_meal_rel: dietPlanData?.diet_plan_meal_rel?.map((meal: any) => ({
    ...meal,
    meal_options: meal?.meal_options?.map((option: any) => ({
      ...option,
      option_data: option?.option_data?.filter(
        (item: any) =>
          item?.is_food_item_added_by_patient === "N" ||
          !item?.hasOwnProperty("is_food_item_added_by_patient"),
      ),
    })),
  })),
});

const transformMealsDataForPdf = (
  requestPayload: any,
  dietPlanId?: string,
  patientId?: string,
) => {
  const mealOptionsFromOptionsData = (optionsData: any[] = []) => {
    const optionsMap = new Map<
      number,
      { option_no: number; option_data: any[] }
    >();

    optionsData.forEach((item) => {
      const optionNo = Number(item?.option_no) || 1;

      if (!optionsMap.has(optionNo)) {
        optionsMap.set(optionNo, { option_no: optionNo, option_data: [] });
      }

      optionsMap.get(optionNo)?.option_data.push({
        food_name: item.food_name,
        quantity: item.quantity,
        unit_option_name: item.unit_option_name || item.unit_option_id,
        tips: item.tips || "",
      });
    });

    return Array.from(optionsMap.values());
  };

  return {
    plan_name: requestPayload?.plan_name,
    patient_id: patientId || requestPayload?.patient_id,
    start_date: requestPayload?.start_date,
    end_date: requestPayload?.end_date,
    total_calories: requestPayload?.total_calories,
    diet_plans_id: dietPlanId || requestPayload?.diet_plans_id,
    diet_plan_meal_rel: (requestPayload?.meals_data || [])
      .filter((meal: any) => meal?.hide_meal !== "Y")
      .map((meal: any) => ({
        meal_type: meal?.meal_name,
        hide_meal: meal?.hide_meal || "N",
        start_time: meal?.start_time,
        end_time: meal?.end_time,
        meal_options: mealOptionsFromOptionsData(meal?.options_data),
      })),
  };
};

const buildPdfDietData = ({
  apiPlanData,
  requestPayload,
  patientId,
}: {
  apiPlanData: any;
  requestPayload?: any;
  patientId: string;
}) => {
  const dietPlanId =
    apiPlanData?.diet_plans_id ||
    apiPlanData?.diet_plan_id ||
    requestPayload?.diet_plans_id ||
    requestPayload?.diet_plan_id;

  if (hasMealPlanContent(apiPlanData)) {
    return sanitizeListFormatPlan({
      ...apiPlanData,
      patient_id: patientId,
      diet_plans_id: dietPlanId,
      plan_name: apiPlanData?.plan_name || requestPayload?.plan_name,
      start_date: apiPlanData?.start_date || requestPayload?.start_date,
      end_date: apiPlanData?.end_date || requestPayload?.end_date,
      total_calories:
        apiPlanData?.total_calories || requestPayload?.total_calories,
    });
  }

  if (requestPayload?.meals?.length) {
    return transformFormDietPlanForPdf(
      {
        ...requestPayload,
        patient_id: patientId,
        diet_name: requestPayload?.diet_name || apiPlanData?.plan_name,
        start_date: requestPayload?.start_date || apiPlanData?.start_date,
        end_date: requestPayload?.end_date || apiPlanData?.end_date,
        calories: requestPayload?.calories || apiPlanData?.total_calories,
      },
      dietPlanId,
    );
  }

  if (requestPayload?.meals_data?.length) {
    return transformMealsDataForPdf(requestPayload, dietPlanId, patientId);
  }

  return sanitizeListFormatPlan({
    ...apiPlanData,
    patient_id: patientId,
    diet_plans_id: dietPlanId,
  });
};

const fetchPatientDetails = async (patientId: string, userDetails?: any) => {
  try {
    const response = await API.GetPatientDetails({
      patient_id: patientId,
      health_coach_id: getHealthCoachId(userDetails),
    });

    return response?.data ?? null;
  } catch (error) {
    console.error("Failed to fetch patient details for diet plan PDF:", error);
    return null;
  }
};

export const uploadDietPlanRecord = async (
  dispatch: AppDispatch,
  {
    planStatus,
    apiResponse,
    dietPlanData,
    requestPayload,
    fallbackPatientId,
    userDetails,
  }: UploadDietPlanRecordParams,
) => {
  const patientId =
    dietPlanData?.patient_id ||
    fallbackPatientId ||
    apiResponse?.data?.patient_id ||
    requestPayload?.patient_id;

  if (!patientId) {
    return false;
  }

  const pdfDietData = buildPdfDietData({
    apiPlanData: dietPlanData,
    requestPayload,
    patientId,
  });

  if (!hasMealPlanContent(pdfDietData)) {
    console.error("Diet plan PDF upload skipped: meal data unavailable");
    return false;
  }

  const patientDetails = await fetchPatientDetails(patientId, userDetails);
  const file = await generateDietPlanPdfFileFromData(
    pdfDietData,
    userDetails,
    patientDetails,
  );
  const { assignedBy, assignedByName } = getLoggedInCoachDetails(userDetails);
  const payload = buildDietPlanRecordPayload({
    patientId,
    fileName: file.name,
    metaData: {
      dietPlanId: pdfDietData?.diet_plans_id,
      planName: pdfDietData?.plan_name,
      planStatus: planStatus,
      planStartDate: pdfDietData?.start_date,
      planEndDate: pdfDietData?.end_date,
      assignedBy,
      assignedByName,
    },
  });

  await new Promise((resolve, reject) => {
    dispatch(
      bulkUploadPatientRecordsRequest({
        formData: buildBulkUploadFormData(payload, [file]),
        silent: true,
        resolve,
        reject,
      }),
    );
  });

  return true;
};
